import type { LifecycleTrackedProposal } from '../types/lifecycle';
import { trackingLevelLabel } from '../utils/lifecycleFormat';
import { RiderBadge } from './RiderBadge';
import { SequenceBadge } from './SequenceSurfacing';
import {
  FirstReadingBadge,
  LeadCommitteeChip,
  LegislativeMiniStepper,
  LegislativeStageBadge,
  NextStepLine,
} from './LegislativePath';

interface TrackedCardProps {
  item: LifecycleTrackedProposal;
  selected: boolean;
  onClick: () => void;
}

export function TrackedCard({ item, selected, onClick }: TrackedCardProps) {
  // PR-E: risk and priority are separate axes. Match the visual mockup by
  // showing the assessed risk when it exists; otherwise keep a neutral priority
  // band instead of inventing an AI risk verdict.
  const hasAssessedRisk = item.risk_score != null && item.risk_level != null;
  const priorityScore = item.tracking_priority_score ?? item.tracking_score;
  const priorityLevel = item.tracking_priority_level ?? item.tracking_level;
  const displayLevel = hasAssessedRisk ? item.risk_level! : priorityLevel;
  const displayScore = hasAssessedRisk ? item.risk_score! : priorityScore;
  const displayLabel = hasAssessedRisk ? trackingLevelLabel(displayLevel) : 'Neposouzeno';
  const riskBandLevel = hasAssessedRisk && displayLevel ? displayLevel : 'unknown';
  const riskBandLabel = displayLabel.toLocaleLowerCase('cs-CZ');

  const legislative = item.legislative_status;
  const hasStageLabel = Boolean(legislative?.stage_label || (!legislative && item.current_stage_label));

  return (
    <div className={`tracked-card tracked-card--risk-${riskBandLevel} ${selected ? 'tracked-card--selected' : ''}`} role="listitem">
      <button type="button" className="tracked-card__btn" onClick={onClick} aria-pressed={selected}>
        <div className="tracked-card__riskband" aria-label={`${hasAssessedRisk ? 'Riziko' : 'Priorita'} ${displayScore}, ${displayLabel}`}>
          <span className="tracked-card__risk-label">{hasAssessedRisk ? 'Riziko' : 'Priorita'}</span>
          <span className="tracked-card__risk-score">{displayScore}</span>
          <span className="tracked-card__risk-level">{riskBandLabel}</span>
        </div>

        <div className="tracked-card__content">
          <div className="tracked-card__top">
            <div className="tracked-card__main">
              <div className="tracked-card__title">{item.title}</div>
              <div className="tracked-card__badges">
                {hasStageLabel ? (
                  <LegislativeStageBadge status={legislative} fallbackLabel={item.current_stage_label} />
                ) : (
                  <span className="badge badge--stage badge--stage-unknown">Fáze neurčena</span>
                )}
                <FirstReadingBadge status={legislative} />
                <LeadCommitteeChip status={legislative} />
                {item.possible_rider?.label && <RiderBadge label={item.possible_rider.label} />}
                {item.sequence_surfacing && <SequenceBadge surfacing={item.sequence_surfacing} />}
              </div>
            </div>
            <span className="tracked-card__chev" aria-hidden="true">›</span>
          </div>

          <LegislativeMiniStepper status={legislative} />
          <NextStepLine nextStep={legislative?.next_step} />
        </div>
      </button>
    </div>
  );
}
